import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTheme } from '../../context/ThemeContext';
import { 
  Cookie, 
  Settings, 
  ChevronDown, 
  ChevronUp, 
  Shield,
  Eye,
  BarChart3,
  Zap,
  Globe,
  X,
  Check,
  Info
} from 'lucide-react';

const CookieConsent = () => {
  const { isDark } = useTheme();
  const [isVisible, setIsVisible] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);
  const [showModal, setShowModal] = useState(false);
  
  // Cookie kategorileri ve durumları
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Her zaman aktif, değiştirilemez
    analytics: true,
    marketing: true,
    functional: true
  });

  // Sayfa yüklendiğinde çerez onay durumunu kontrol et
  useEffect(() => {
    const cookieConsent = localStorage.getItem('cookieConsent');
    if (!cookieConsent) {
      // İlk ziyaretse banner'ı göster
      setTimeout(() => setIsVisible(true), 1000);
    }
  }, []);

  // Çerez kategorileri bilgileri
  const cookieCategories = [
    {
      id: 'necessary',
      name: 'Gerekli Çerezler',
      description: 'Web sitesinin temel işlevleri için gereklidir',
      icon: <Shield className="w-5 h-5" />,
      required: true,
      examples: ['Oturum yönetimi', 'Güvenlik', 'Kullanıcı tercihleri'],
      details: 'Bu çerezler web sitesinin çalışması için mutlaka gereklidir. Bu çerezler olmadan site düzgün çalışmaz.'
    },
    {
      id: 'analytics',
      name: 'Analitik Çerezler',
      description: 'Web sitesi kullanımını analiz etmek için',
      icon: <BarChart3 className="w-5 h-5" />,
      required: false,
      examples: ['Sayfa görüntülenmeleri', 'Kullanıcı davranışları', 'Performans verileri'],
      details: 'Site kullanımını anlamamıza yardımcı olan anonim istatistik verileri toplar.'
    },
    {
      id: 'marketing',
      name: 'Pazarlama Çerezleri',
      description: 'Size uygun reklamlar göstermek için',
      icon: <Zap className="w-5 h-5" />,
      required: false,
      examples: ['Hedefli reklamlar', 'Sosyal medya entegrasyonu', 'Kişiselleştirilmiş içerik'],
      details: 'İlginizi çekebilecek reklamları ve içerikleri göstermek için kullanılır.'
    },
    {
      id: 'functional',
      name: 'İşlevsel Çerezler',
      description: 'Ek özellikler ve kişiselleştirme için',
      icon: <Settings className="w-5 h-5" />,
      required: false,
      examples: ['Dil seçimi', 'Tema tercihleri', 'Form verileri'],
      details: 'Web sitesi deneyiminizi iyileştiren gelişmiş özellikler için kullanılır.'
    }
  ];

  // Çerez tercihi değiştirme
  const handlePreferenceChange = (category, value) => {
    if (category === 'necessary') return; // Gerekli çerezler değiştirilemez
    
    setCookiePreferences(prev => ({
      ...prev,
      [category]: value
    }));
  };

  // Tümünü kabul et
  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      functional: true
    };
    
    setCookiePreferences(allAccepted);
    saveCookieConsent(allAccepted);
    setIsVisible(false);
    setShowModal(false);
  };

  // Tümünü reddet (sadece gerekli çerezler)
  const rejectAll = () => {
    const onlyNecessary = {
      necessary: true,
      analytics: false,
      marketing: false,
      functional: false
    };
    
    setCookiePreferences(onlyNecessary);
    saveCookieConsent(onlyNecessary);
    setIsVisible(false);
    setShowModal(false);
  };

  // Seçilenleri kaydet
  const saveSelected = () => {
    saveCookieConsent(cookiePreferences);
    setIsVisible(false);
    setShowModal(false);
  };

  // Çerez onayını localStorage'a kaydet
  const saveCookieConsent = (preferences) => {
    const consentData = {
      preferences,
      timestamp: new Date().toISOString(),
      version: '1.0'
    };
    
    localStorage.setItem('cookieConsent', JSON.stringify(consentData));
    
    // Google Analytics vb. servisleri başlat/durdur
    if (preferences.analytics) {
      // Analytics kodunu burada etkinleştir
      console.log('Analytics enabled');
    }
    
    if (preferences.marketing) {
      // Marketing kodunu burada etkinleştir
      console.log('Marketing enabled');
    }
  };

  // Modal açma
  const openAdvancedSettings = () => {
    setShowModal(true);
    setShowAdvanced(false);
  };

  if (!isVisible) return null;

  return (
    <AnimatePresence>
      <div className="fixed inset-0 z-50 flex items-end justify-center p-2 sm:p-4">
        {/* Backdrop */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="absolute inset-0 bg-black/20 backdrop-blur-sm"
          onClick={() => setShowModal(false)}
        />

        {/* Cookie Banner */}
        <motion.div
          initial={{ opacity: 0, y: 100, scale: 0.95 }}
          animate={{ opacity: 1, y: 0, scale: 1 }}
          exit={{ opacity: 0, y: 100, scale: 0.95 }}
          transition={{ type: "spring", duration: 0.5 }}
          className={`relative w-full max-w-4xl max-h-[90vh] overflow-hidden ${
            isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
          } border rounded-lg sm:rounded-2xl shadow-2xl flex flex-col`}
        >
          {/* Header */}
          <div className={`p-3 sm:p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex-shrink-0`}>
            <div className="flex items-start justify-between">
              <div className="flex items-start sm:items-center">
                <div className={`p-2 sm:p-3 rounded-lg sm:rounded-xl ${isDark ? 'bg-blue-900/30' : 'bg-blue-100'} mr-2 sm:mr-4 flex-shrink-0`}>
                  <Cookie className={`w-4 h-4 sm:w-6 sm:h-6 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                </div>
                <div className="min-w-0 flex-1">
                  <h2 className="text-lg sm:text-xl font-bold mb-1">Gizlilik Ayarları</h2>
                  <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} leading-relaxed`}>
                    Bu site, sizi <strong>kişisel olarak tanımak</strong> ve web sitemizde ve internetteki 
                    diğer yerlerdeki eylemlerinizi analiz etmek için çeşitli teknolojiler kullanır.
                  </p>
                </div>
              </div>
              <div className={`px-2 py-1 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
              }`}>
                GDPR
              </div>
            </div>
          </div>

          {/* Content - Scrollable */}
          <div className="flex-1 overflow-y-auto">
            <div className="p-3 sm:p-6">
              <div className="text-xs sm:text-sm mb-4 sm:mb-6">
                <p className="mb-2 sm:mb-3 leading-relaxed">
                  Bu site, deneyiminizi iyileştirmek için çerezler kullanır. Tüm çerezleri kabul edebilir, 
                  sadece gerekli olanları seçebilir veya detaylı ayarlar yapabilirsiniz.
                </p>
                <div className={`mt-3 p-3 rounded-lg ${isDark ? 'bg-blue-900/20' : 'bg-blue-50'} border-l-4 ${isDark ? 'border-blue-400' : 'border-blue-500'}`}>
                  <div className="flex">
                    <Info className={`w-4 h-4 ${isDark ? 'text-blue-400' : 'text-blue-600'} mr-2 flex-shrink-0 mt-0.5`} />
                    <p className={`text-xs sm:text-sm ${isDark ? 'text-blue-300' : 'text-blue-700'}`}>
                      <strong>Onayınız isteğe bağlıdır</strong> ve istediğiniz zaman değiştirebilirsiniz.
                    </p>
                  </div>
                </div>
              </div>

              {/* Simplified Info - No complex checkboxes on first view */}
              <div className="mb-4 sm:mb-6">
                <div className={`p-4 rounded-lg ${isDark ? 'bg-gray-800/50 border-gray-700' : 'bg-gray-50 border-gray-200'} border`}>
                  <div className="flex items-center mb-3">
                    <Cookie className={`w-5 h-5 ${isDark ? 'text-gray-400' : 'text-gray-600'} mr-2`} />
                    <span className="font-medium text-sm">Kullanılan Çerez Türleri</span>
                  </div>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-xs">
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${isDark ? 'bg-green-400' : 'bg-green-500'}`}></div>
                      <span>Gerekli</span>
                    </div>
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${isDark ? 'bg-blue-400' : 'bg-blue-500'}`}></div>
                      <span>Analitik</span>
                    </div>
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${isDark ? 'bg-purple-400' : 'bg-purple-500'}`}></div>
                      <span>Pazarlama</span>
                    </div>
                    <div className="flex items-center">
                      <div className={`w-2 h-2 rounded-full mr-2 ${isDark ? 'bg-orange-400' : 'bg-orange-500'}`}></div>
                      <span>İşlevsel</span>
                    </div>
                  </div>
                  <p className={`mt-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-600'}`}>
                    Detaylı ayarlar için "Özelleştir" butonunu kullanabilirsiniz.
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Action Buttons - Simplified and Clear */}
          <div className={`p-4 sm:p-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex-shrink-0`}>
            {/* Main Action Buttons */}
            <div className="flex flex-col space-y-3 sm:space-y-0 sm:flex-row sm:justify-center sm:space-x-4">
              {/* Primary Actions */}
              <button
                onClick={acceptAll}
                className="w-full sm:w-auto px-6 sm:px-8 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium text-sm shadow-lg hover:shadow-xl transform hover:scale-105 transition-transform duration-200"
              >
                ✓ Tümünü Kabul Et
              </button>
              
              <button
                onClick={rejectAll}
                className={`w-full sm:w-auto px-6 sm:px-8 py-3 border-2 rounded-lg font-medium text-sm transition-colors ${
                  isDark 
                    ? 'border-gray-600 text-gray-300 hover:bg-gray-700 hover:border-gray-500' 
                    : 'border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400'
                }`}
              >
                Sadece Gerekli
              </button>
            </div>

            {/* Secondary Action */}
            <div className="mt-3 text-center">
              <button
                onClick={openAdvancedSettings}
                className={`inline-flex items-center px-4 py-2 text-sm ${
                  isDark 
                    ? 'text-gray-400 hover:text-gray-300' 
                    : 'text-gray-600 hover:text-gray-800'
                } transition-colors underline hover:no-underline`}
              >
                <Settings className="w-4 h-4 mr-1" />
                Özelleştir
              </button>
            </div>

            {/* Trust Indicators */}
            <div className="mt-4 pt-3 border-t border-gray-200 dark:border-gray-700">
              <div className="flex items-center justify-center space-x-4 text-xs">
                <div className="flex items-center">
                  <Shield className={`w-3 h-3 mr-1 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>GDPR Uyumlu</span>
                </div>
                <div className="flex items-center">
                  <Eye className={`w-3 h-3 mr-1 ${isDark ? 'text-blue-400' : 'text-blue-600'}`} />
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Şeffaf</span>
                </div>
                <div className="flex items-center">
                  <Check className={`w-3 h-3 mr-1 ${isDark ? 'text-green-400' : 'text-green-600'}`} />
                  <span className={isDark ? 'text-gray-400' : 'text-gray-600'}>Güvenli</span>
                </div>
              </div>
            </div>
          </div>

          {/* Powered by - Mobile Hidden or Simplified */}
          <div className={`hidden sm:block px-6 py-3 border-t ${isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50/50'} text-center`}>
            <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>
              Powered by{' '}
              <a href="#" className="text-blue-600 hover:text-blue-700 underline">
                Consent Management
              </a>
            </p>
          </div>
        </motion.div>

        {/* Advanced Settings Modal - Mobile Optimized */}
        <AnimatePresence>
          {showModal && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className={`absolute inset-2 sm:inset-4 ${
                isDark ? 'bg-gray-800 border-gray-700' : 'bg-white border-gray-200'
              } border rounded-lg sm:rounded-2xl shadow-2xl overflow-hidden flex flex-col max-h-[95vh]`}
            >
              {/* Modal Header */}
              <div className={`p-4 sm:p-6 border-b ${isDark ? 'border-gray-700' : 'border-gray-200'} flex-shrink-0`}>
                <div className="flex items-center justify-between">
                  <h3 className="text-lg sm:text-xl font-bold">Çerez Ayarları</h3>
                  <button
                    onClick={() => setShowModal(false)}
                    className={`p-2 rounded-lg ${
                      isDark ? 'hover:bg-gray-700' : 'hover:bg-gray-100'
                    } transition-colors`}
                  >
                    <X className="w-4 h-4 sm:w-5 sm:h-5" />
                  </button>
                </div>
              </div>

              {/* Modal Content - Scrollable */}
              <div className="flex-1 overflow-y-auto p-4 sm:p-6">
                <div className="space-y-4 sm:space-y-6">
                  {cookieCategories.map((category) => (
                    <div
                      key={category.id}
                      className={`p-3 sm:p-4 border rounded-lg sm:rounded-xl ${
                        isDark ? 'border-gray-700 bg-gray-800/50' : 'border-gray-200 bg-gray-50/50'
                      }`}
                    >
                      <div className="flex items-start justify-between mb-2 sm:mb-3">
                        <div className="flex items-start sm:items-center flex-1 min-w-0">
                          <div className={`p-1.5 sm:p-2 rounded-lg ${
                            isDark ? 'bg-gray-700' : 'bg-white'
                          } mr-2 sm:mr-3 flex-shrink-0`}>
                            {React.cloneElement(category.icon, { 
                              className: 'w-4 h-4 sm:w-5 sm:h-5' 
                            })}
                          </div>
                          <div className="min-w-0 flex-1">
                            <h4 className="font-semibold text-sm sm:text-lg">{category.name}</h4>
                            <p className={`text-xs sm:text-sm ${isDark ? 'text-gray-400' : 'text-gray-600'} leading-relaxed`}>
                              {category.description}
                            </p>
                          </div>
                        </div>
                        <div className="flex items-center ml-2 flex-shrink-0">
                          {category.required ? (
                            <span className={`px-2 py-1 text-xs rounded-full ${
                              isDark ? 'bg-green-900/30 text-green-400' : 'bg-green-100 text-green-700'
                            }`}>
                              Gerekli
                            </span>
                          ) : (
                            <label className="relative inline-flex items-center cursor-pointer">
                              <input
                                type="checkbox"
                                checked={cookiePreferences[category.id]}
                                onChange={(e) => handlePreferenceChange(category.id, e.target.checked)}
                                className="sr-only peer"
                              />
                              <div className="relative w-9 h-5 sm:w-11 sm:h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-blue-300 dark:peer-focus:ring-blue-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-4 after:w-4 sm:after:h-5 sm:after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-blue-600"></div>
                            </label>
                          )}
                        </div>
                      </div>

                      <p className={`text-xs sm:text-sm mb-2 sm:mb-3 ${isDark ? 'text-gray-300' : 'text-gray-600'} leading-relaxed`}>
                        {category.details}
                      </p>

                      <div className="text-xs">
                        <strong>Örnekler:</strong>
                        <ul className={`list-disc list-inside mt-1 ${isDark ? 'text-gray-400' : 'text-gray-500'} space-y-0.5`}>
                          {category.examples.map((example, index) => (
                            <li key={index}>{example}</li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Modal Footer */}
              <div className={`p-4 sm:p-6 border-t ${isDark ? 'border-gray-700' : 'border-gray-200'} flex-shrink-0`}>
                <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:justify-between">
                  <button
                    onClick={rejectAll}
                    className={`px-4 sm:px-6 py-2 border rounded-lg text-sm ${
                      isDark 
                        ? 'border-gray-600 text-gray-300 hover:bg-gray-700' 
                        : 'border-gray-300 text-gray-700 hover:bg-gray-50'
                    } transition-colors`}
                  >
                    Sadece Gerekli
                  </button>
                  <div className="flex space-x-2 sm:space-x-3">
                    <button
                      onClick={saveSelected}
                      className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-gray-600 hover:bg-gray-700 text-white rounded-lg transition-colors text-sm"
                    >
                      Seçilenleri Kaydet
                    </button>
                    <button
                      onClick={acceptAll}
                      className="flex-1 sm:flex-none px-4 sm:px-6 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors text-sm font-medium"
                    >
                      Tümünü Kabul Et
                    </button>
                  </div>
                </div>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>
    </AnimatePresence>
  );
};

export default CookieConsent; 